import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classnames from 'classnames';
import { withStyles } from 'theme/withStyles';
import { VALIDATION_STATUS } from 'config/ui';
import Field from 'containers/Field';
import RawHtml from 'common/RawHtml';
import Spinner from 'common/Spinner';
import pageActions from 'core/page/actions';
import Button from 'common/Button';

import headerStyles from './styles/header';
import titleStyles from './styles/title';
import contentWrapperStyles from './styles/contentWrapper';
import dialogStyles from './styles/dialog';
import contentStyles from './styles/content';
import bodyStyles from './styles/body';

const styles = (theme) => {
  const common = {
    body: bodyStyles(theme),
    content: contentStyles(theme),
    contentWrapper: {
      ...contentWrapperStyles(theme),
      '& .col-sm-12': {
        margin: [0, 'auto'],
        maxWidth: 435,
      },
      '& p': {
        margin: [0, 'auto'],
        maxWidth: 475,
        textAlign: 'center',
        [`@media(max-width: ${theme.screenSizes.mobileMax}px)`]: {
          textAlign: 'left',
        },
      },
      [`@media(max-width: ${theme.screenSizes.mobileMax}px)`]: {
        padding: 20,
      },
    },
    dialog: dialogStyles(theme),
    header: headerStyles(theme),
    title: titleStyles(theme),
  };
  return {
    ...common,
    buttonRow: {
      display: 'flex',
      justifyContent: 'center',
      padding: [theme.spacing.sm, 10, 0, 10],
    },
    footer: {
      display: 'none',
    },
  };
};

export const MobileValidationBare = ({
  classes,
  commonClasses,
  disableResend,
  errorMsg,
  fieldId,
  loading,
  mobileValidationActionLabel,
  mobileValidationCodeNotReceivedActionLabel,
  mobileValidationPopupAreaText,
  mobileValidationPopupTitle,
  mobileValidationResend,
  mobileValidationStatus,
  mobileValidationSubmit,
  onContinueClick,
  otpTimeOutSec,
  resendClicked,
  successMsg,
  verificationCode,
}) => {
  const [time, setTime] = useState(otpTimeOutSec);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const hasTime = otpTimeOutSec !== undefined;
  useEffect(() => {
    setTime(otpTimeOutSec);
  }, [otpTimeOutSec, resendClicked]);
  useEffect(() => {
    let timeout;
    if (hasTime) {
      timeout = setTimeout(() => {
        if (!loading) {
          if (time <= 0 && !hasSubmitted) {
            mobileValidationSubmit(verificationCode);
            setHasSubmitted(true);
            clearTimeout(timeout);
          } else if (!hasSubmitted) {
            setTime((currTime) => currTime - 1);
          } else {
            clearTimeout(timeout);
          }
        }
      }, 1000);
    }
    return () => clearTimeout(timeout);
  }, [hasSubmitted, hasTime, loading, mobileValidationSubmit, time, verificationCode]);

  const isInconclusive =
    mobileValidationStatus === VALIDATION_STATUS.MOBILE_VALIDATION_INCONCLUSIVE;
  const isDeclined = mobileValidationStatus === VALIDATION_STATUS.MOBILE_VALIDATION_DECLINED;

  const inclusiveButtons = [
    {
      className: classnames('btn btn-mobile btn-verify-verificationCode', { loading }),
      'data-name': 'btn-resend-verificationCode',
      disabled: loading || !verificationCode,
      onClick: () => mobileValidationSubmit(verificationCode),
      text: mobileValidationActionLabel,
      variant: 'primary',
    },
    {
      className: 'btn btn-tertiary btn-resend-verificationCode',
      'data-name': 'btn-resend-verificationCode',
      disabled: loading || disableResend,
      onClick: () => {
        mobileValidationResend();
        setTime(otpTimeOutSec);
        setHasSubmitted(false);
      },
      text: mobileValidationCodeNotReceivedActionLabel,
      variant: 'secondary',
    },
  ];

  return (
    <div className={classnames('modal-verify-verificationCode', commonClasses, classes.dialog)}>
      <div className={classnames('modal-content', classes.content)}>
        <div className={classnames('modal-header', classes.header)}>
          <RawHtml type="h4" className={classnames(classes.title, 'modal-title')} id="modal-title">
            {mobileValidationPopupTitle}
          </RawHtml>
        </div>
        <div className="error-region">{errorMsg}</div>
        <div className={classnames('modal-body', classes.body)} id="modal-body">
          <div
            className={classnames(
              'modal-content-wrapper verify-verificationCode',
              classes.contentWrapper,
            )}
          >
            <RawHtml type="p">{mobileValidationPopupAreaText}</RawHtml>
            {successMsg && <p className="success-msg">{successMsg}</p>}
            {hasTime && <p>You have {time} seconds to complete this form.</p>}
            <div className="row">
              <div className="widget">
                <div className="region-groups">
                  <Field fieldName={fieldId} groupName="PRIMARY_MOBILE_VERIFICATION" />
                </div>
              </div>
            </div>
            <div className={classnames('verificationCode-btn-group', classes.buttonRow)}>
              {isInconclusive &&
                inclusiveButtons.map((b, i) => (
                  <Button
                    /* eslint-disable-next-line react/no-array-index-key */
                    key={`dialog-button-${i}`}
                    {...b}
                  >
                    {b.text}
                    {b.variant === 'primary' && (
                      <>
                        <i className="icon-arrow-thin-right" aria-hidden="true" />
                        <Spinner loading={loading} />
                      </>
                    )}
                  </Button>
                ))}
              {isDeclined && (
                <RawHtml
                  className={classnames('btn btn-mobile btn-verify-verificationCode', { loading })}
                  data-name="btn-verify-verificationCode"
                  type="button"
                  disabled={loading}
                  onClick={onContinueClick}
                >
                  {`${mobileValidationActionLabel} <i class="icon-arrow-thin-right" aria-hidden="true"></i><i class="spinner" aria-hidden="true"></i>`}
                </RawHtml>
              )}
            </div>
          </div>
        </div>
        <div className={classnames('modal-footer', classes.footer)} />
      </div>
    </div>
  );
};

MobileValidationBare.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  commonClasses: PropTypes.string.isRequired,
  disableResend: PropTypes.bool,
  errorMsg: PropTypes.string,
  fieldId: PropTypes.string.isRequired,
  loading: PropTypes.bool,
  mobileValidationActionLabel: PropTypes.string,
  mobileValidationCodeNotReceivedActionLabel: PropTypes.string,
  mobileValidationPopupAreaText: PropTypes.string,
  mobileValidationPopupTitle: PropTypes.string,
  mobileValidationResend: PropTypes.func.isRequired,
  mobileValidationStatus: PropTypes.string.isRequired,
  mobileValidationSubmit: PropTypes.func.isRequired,
  onContinueClick: PropTypes.func.isRequired,
  otpTimeOutSec: PropTypes.number,
  resendClicked: PropTypes.number,
  successMsg: PropTypes.string,
  verificationCode: PropTypes.string,
};

MobileValidationBare.defaultProps = {
  disableResend: false,
  errorMsg: '',
  loading: false,
  mobileValidationActionLabel: '',
  mobileValidationCodeNotReceivedActionLabel: '',
  mobileValidationPopupAreaText: '',
  mobileValidationPopupTitle: '',
  otpTimeOutSec: undefined,
  resendClicked: 0,
  successMsg: '',
  verificationCode: '',
};

const mapStateToProps = ({ configs, data, dialogs, resources, ui }) => {
  const isUserAuth = dialogs.type === 'mobilevalidation_userauth';
  const common = {
    disableResend: dialogs.params && dialogs.params.disableResend,
    errorMsg: dialogs.params && dialogs.params.errorMsg,
    loading: ui.pageLoading,
    mobileValidationStatus: data.reason || VALIDATION_STATUS.MOBILE_VALIDATION_INCONCLUSIVE,
    otpTimeOutSec: configs.otpTimeOutSec,
    resendClicked: dialogs.params?.resendClicked,
    successMsg: dialogs.params?.successMsg,
  };
  if (isUserAuth) {
    return {
      ...common,
      fieldId: 'appRequest_mfa_verificationCode',
      mobileValidationActionLabel: resources.mfaActionSendCodeSubmitLabel,
      mobileValidationCodeNotReceivedActionLabel: resources.mfaActionResendCodeLabel,
      mobileValidationPopupAreaText: resources.mfaAuthCodeAreaText,
      mobileValidationPopupTitle: resources.mfaPopupTitle,
      verificationCode: data.appRequest_mfa_verificationCode,
    };
  }
  return {
    ...common,
    fieldId: data.clientId || 'appRequest_mobileValidation',
    mobileValidationActionLabel: resources.mobileValidationActionLabel,
    mobileValidationCodeNotReceivedActionLabel:
      resources.mobileValidationCodeNotReceivedActionLabel,
    mobileValidationPopupAreaText:
      data.reason === VALIDATION_STATUS.MOBILE_VALIDATION_INCONCLUSIVE
        ? resources.mobileValidationInconclusivePopupAreaText
        : resources.mobileValidationDeclinedPopupAreaText,
    mobileValidationPopupTitle: resources.mobileValidationPopupTitle,
    verificationCode: data[data.clientId || 'appRequest_mobileValidation'],
  };
};

const mapDispatchToProps = {
  mobileValidationResend: pageActions.mobileValidationResend,
  mobileValidationSubmit: pageActions.mobileValidationSubmit,
  onContinueClick: pageActions.next,
};

const HomeMobileValidationStyled = withStyles(styles)(MobileValidationBare);

export default connect(mapStateToProps, mapDispatchToProps)(HomeMobileValidationStyled);
